/* eslint-disable no-useless-escape */
import { useContext, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import {
  Button,
  Card,
  CardContent,
  Container,
  Link as MuiLink,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, useParams } from "react-router-dom";

// types/services
import type { RegistrationRequest } from "../../services/model/registrationRequest";
import { registerDetails } from "../../services/api/user-service";
import { useConfirm } from "@/components/dialog/confirm";
import EnvironmentFlag from "@/components/environmentFlag";
import BrandLoader from "@/branding/BrandLoader";
import BrandLogo from "@/branding/BrandLogo";
import { BrandingContext } from "@/branding/BrandingProvider";

interface RegisterFormValues {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  confirmEmail: string;
  policyNumber: string;
  idNumber: string;
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
}

export default function Register() {
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const branding = useContext(BrandingContext);

  const goToTenantLogin = () =>
    navigate(tenant ? `/${tenant}/login` : "/login", { replace: true });

  const showDialog = (message: string, success: boolean) => {
    if (success) {
      const description = `${message}. We’ve sent a confirmation email. Please click the link in that email before logging in.`;

      confirm({
        catchOnCancel: false,
        title: "Registration",
        description,
      }).then(() => {
        // after user closes the dialog, send them to login
        goToTenantLogin();
      });
    } else {
      confirm({
        catchOnCancel: false,
        title: "Registration Failed",
        description:
          "It seems your user already exist, you can reset your password or retry?",
        okOnly: false,
        confirmText: "Forgot Password",
        cancelText: "Retry",
      }).then(() => {
        navigate(tenant ? `/${tenant}/forgotpassword` : "/forgotpassword", {
          replace: true,
        });
      });
    }
  };

  // validation regexes (kept from your original)
  const numberRegex: RegExp = /^(\+\d{1,4}[- ]?)?\d{7,}$/;
  const policyRegex: RegExp = /^[a-zA-Z0-9\-(@\!/\#$%^&*())_]{1,}$/;
  const idRegex: RegExp = /^[a-zA-Z0-9\-(\/\\#]{1,}$/;
  // At least 12 chars, one lowercase, one uppercase, one digit, one non-alphanumeric
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;

  const emailRegex: RegExp =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

  const initialValues: RegisterFormValues = {
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    confirmEmail: "",
    policyNumber: "",
    idNumber: "",
    password: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          firstName: Yup.string().required("Name is required"),
          lastName: Yup.string().required("Surname is required"),
          mobileNumber: Yup.string()
            .matches(numberRegex, "Mobile number is not valid")
            .min(10, "Phone number is invalid")
            .required("Phone number is required"),
          email: Yup.string()
            .matches(emailRegex, "Email address is not valid")
            .required("Email address is required"),
          confirmEmail: Yup.string()
            .oneOf([Yup.ref("email")], "Email addresses must match")
            .required("Confirm Email address is required"),
          policyNumber: Yup.string()
            .matches(policyRegex, "Policy number is not valid")
            .required("Policy number is required"),
          idNumber: Yup.string()
            .matches(idRegex, "ID number is not valid")
            .required("ID number is required"),
          password: Yup.string()
            .matches(
              passwordRegex,
              "Password must be at least 12 characters and include at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character"
            )
            .required("Password is required"),
          confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords must match")
            .required("Confirm Password is required"),
        })}
        onSubmit={async (fields) => {
          try {
            setLoading(true);

            const payload: RegistrationRequest = {
              firstName: fields.firstName,
              lastName: fields.lastName,
              mobileNumber: fields.mobileNumber,
              email: fields.email,
              policyNumber: fields.policyNumber,
              idNumber: fields.idNumber,
              password: fields.password,
              divisionId: "00000000-0000-0000-0000-000000000000",
              clientId: branding?.clientId,
            };

            const response = await registerDetails(payload);
            if (response) {
              showDialog(response.message, response.resultCode);
            } else {
              showDialog(
                "There seems to be a problem with your internet connection. Please check your connection and try again.",
                false
              );
            }
          } catch (err) {
            showDialog(
              "An exception occurred, please contact your administrator.",
              false
            );
            if (err instanceof Error) console.error(err.message);
          } finally {
            setLoading(false);
          }
        }}
      >
        {(formik) => {
          const {
            values,
            setValues,
            touched,
            errors,
            handleChange,
            handleBlur,
          } = formik;

          return (
            <>
              <Container maxWidth="sm" sx={{ pt: 6 }}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <EnvironmentFlag />
                        <Grid container justifyContent="center">
                          <BrandLogo size={150} shape="circle" ring shadow />
                        </Grid>
                        <Box sx={{ height: 60 }} />
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          direction="column"
                          sx={{ mt: 1 }}
                        >
                          <Typography align="center" variant="subtitle1">
                            Please note that your email address will be used as
                            your username.
                          </Typography>
                        </Grid>

                        <Grid size={12} sx={{ my: 2 }} />

                        <Form noValidate>
                          <Box display="flex" flexDirection="column" gap={1}>
                            <TextField
                              type="text"
                              label="Email Address"
                              name="email"
                              placeholder="Eg. johndoe@anonymous.com"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={!!(errors.email && touched.email)}
                              helperText={touched.email && errors.email}
                            />
                            <TextField
                              type="text"
                              label="Confirm Email Address"
                              name="confirmEmail"
                              placeholder="Re-enter your email address"
                              value={values.confirmEmail}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={
                                !!(errors.confirmEmail && touched.confirmEmail)
                              }
                              helperText={
                                touched.confirmEmail && errors.confirmEmail
                              }
                            />
                            <TextField
                              type="text"
                              label="Name"
                              name="firstName"
                              value={values.firstName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={!!(errors.firstName && touched.firstName)}
                              helperText={touched.firstName && errors.firstName}
                            />
                            <TextField
                              type="text"
                              label="Surname"
                              name="lastName"
                              value={values.lastName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={!!(errors.lastName && touched.lastName)}
                              helperText={touched.lastName && errors.lastName}
                            />
                            <TextField
                              type="text"
                              label="Mobile Number"
                              name="mobileNumber"
                              placeholder="Eg. +2712 3456789; +27 123456789; 0123456789"
                              value={values.mobileNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={
                                !!(errors.mobileNumber && touched.mobileNumber)
                              }
                              helperText={
                                touched.mobileNumber && errors.mobileNumber
                              }
                            />
                            <TextField
                              type="text"
                              label="Policy Number"
                              name="policyNumber"
                              value={values.policyNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={
                                !!(errors.policyNumber && touched.policyNumber)
                              }
                              helperText={
                                touched.policyNumber && errors.policyNumber
                              }
                            />
                            <TextField
                              type="text"
                              label="ID Number"
                              name="idNumber"
                              value={values.idNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={!!(errors.idNumber && touched.idNumber)}
                              helperText={touched.idNumber && errors.idNumber}
                            />
                            <TextField
                              type={values.showPassword ? "text" : "password"}
                              label="Password"
                              name="password"
                              value={values.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={!!(errors.password && touched.password)}
                              helperText={touched.password && errors.password}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() =>
                                        setValues({
                                          ...values,
                                          showPassword: !values.showPassword,
                                        })
                                      }
                                      aria-label="toggle password visibility"
                                      edge="end"
                                    >
                                      {values.showPassword ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <TextField
                              type={
                                values.showConfirmPassword ? "text" : "password"
                              }
                              label="Confirm Password"
                              name="confirmPassword"
                              value={values.confirmPassword}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={
                                !!(
                                  errors.confirmPassword &&
                                  touched.confirmPassword
                                )
                              }
                              helperText={
                                touched.confirmPassword &&
                                errors.confirmPassword
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() =>
                                        setValues({
                                          ...values,
                                          showConfirmPassword:
                                            !values.showConfirmPassword,
                                        })
                                      }
                                      aria-label="toggle password visibility"
                                      edge="end"
                                    >
                                      {values.showConfirmPassword ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {branding?.termsAndConditions && (
                              <Box sx={{ mt: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={termsAccepted}
                                      onChange={(e) =>
                                        setTermsAccepted(e.target.checked)
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      I have read and understood the{" "}
                                      <MuiLink
                                        component="button"
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setTermsModalOpen(true);
                                        }}
                                        underline="always"
                                        color="secondary"
                                        sx={{
                                          verticalAlign: "baseline",
                                          padding: 0,
                                          border: "none",
                                          background: "none",
                                        }}
                                      >
                                        Terms & Conditions
                                      </MuiLink>
                                    </Typography>
                                  }
                                />
                              </Box>
                            )}

                            <Button
                              type="submit"
                              value="Register"
                              variant="contained"
                              color="primary"
                              fullWidth
                              size="large"
                              disabled={
                                !!(
                                  branding?.termsAndConditions && !termsAccepted
                                )
                              }
                            >
                              <strong>Register</strong>
                            </Button>
                          </Box>
                        </Form>

                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          direction="column"
                          sx={{ mt: 2 }}
                        >
                          <Typography>
                            <MuiLink
                              component="button"
                              onClick={goToTenantLogin}
                              underline="always"
                              color="secondary"
                            >
                              <strong>Already Registered - Click Here</strong>
                            </MuiLink>
                          </Typography>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
              <BrandLoader open={loading} />

              {/* Terms & Conditions Modal */}
              <Dialog
                open={termsModalOpen}
                onClose={() => setTermsModalOpen(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
              >
                <DialogTitle>Terms & Conditions</DialogTitle>
                <DialogContent dividers>
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: branding?.termsAndConditions || "",
                    }}
                    sx={{
                      "& p": { mb: 1 },
                      "& h1, & h2, & h3": { mt: 2, mb: 1 },
                      "& ul, & ol": { pl: 3, mb: 1 },
                      "& a": { color: "secondary.main" },
                    }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => setTermsModalOpen(false)}
                    color="secondary"
                    variant="outlined"
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          );
        }}
      </Formik>
    </div>
  );
}
